import React, { useEffect, useState, Suspense } from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
// ✅ CRITICAL: Import directly, not from @/providers barrel
// The barrel might re-export other providers that pull in extra code
import { ProviderComposition } from "@/providers/ProviderComposition";
// ✅ CRITICAL: Import directly from source, not from @/contexts barrel
import { useAuth } from "@/features/identity/contexts/AuthContext";
import {
  publicRoutes,
  dashboardRoutes,
  settingsRoutes,
  platformAdminRoutes,
  isPublicRoute,
} from "@/services/shared/config/routes";
// ✅ CRITICAL: Lazy-load layouts to prevent loading dashboard components on login screen
// Even though layouts are only rendered conditionally, eager imports cause Vite to analyze them
const DashboardLayout = React.lazy(() => import("@/layouts/DashboardLayout"));
const SettingsLayout = React.lazy(() => import("@/layouts/SettingsLayout"));
const PlatformAdminLayout = React.lazy(
  () => import("@/layouts/PlatformAdminLayout"),
);
// ✅ CRITICAL: Import ProtectedRoute directly, not from barrel export
// The barrel export includes views/index.ts which exports OnboardingView
// OnboardingView imports @/ui/shared barrel, loading ALL ui/shared components
import { ProtectedRoute } from "@/features/identity/components/ProtectedRoute";
import { applyThemeForScope } from "@/services/shared/utils/theme";
import { SectionLoading } from "@/ui/primitives";

/**
 * Suspense wrapper for lazy-loaded route components.
 * Light layout skeleton — not a blank centered spinner.
 */
function LazyRouteWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-primary p-6 sm:p-8">
          <div className="mx-auto max-w-5xl space-y-4">
            <SectionLoading variant="block" label="Loading page" />
          </div>
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

// Root redirect component
function RootRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    const hasToken = !!localStorage.getItem("auth_token");
    if (hasToken) {
      navigate("/home", { replace: true });
    } else {
      navigate("/login", { replace: true });
    }
  }, [navigate]);
  return null;
}

function AppRoutes() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isPending, user } = useAuth();
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  // Check if public route using centralized config
  const isPublic = isPublicRoute(location.pathname);

  // Public pages always use the OS theme; protected routes use user preference
  useEffect(() => {
    applyThemeForScope(isPublic ? "public" : "protected");
  }, [isPublic]);

  // 🔍 DIAGNOSTIC LOGGING
  useEffect(() => {}, [
    location.pathname,
    isPublic,
    isAuthenticated,
    isPending,
    user,
    loadingTimeout,
  ]);

  // Safety timeout: if loading takes more than 5 seconds, allow navigation
  useEffect(() => {
    if (!isPublic && isPending) {
      const timeout = setTimeout(() => {
        setLoadingTimeout(true);
      }, 5000); // 5 second safety timeout
      return () => {
        clearTimeout(timeout);
      };
    } else {
      setLoadingTimeout(false);
    }
  }, [isPublic, isPending]);

  // CRITICAL FIX: Block routes until auth check completes AND user is loaded
  // Check if we have token but no user (auth check in progress)
  // isAuthenticated now requires user object, so if token exists but no user, we're not authenticated yet
  const hasTokenButNoUser =
    typeof window !== "undefined" &&
    !!localStorage.getItem("auth_token") &&
    !user;

  // Show loading for protected routes if:
  // 1. Auth check is pending, OR
  // 2. We have a token but no user (auth check in progress)
  // BUT: Don't block forever - use safety timeout
  // Also: Don't block public routes even if auth is pending
  // FIX: isAuthenticated now requires user, so we block until user is loaded
  if (!isPublic && (isPending || hasTokenButNoUser) && !loadingTimeout) {
    return (
      <div className="min-h-screen bg-primary flex items-center justify-center">
        <div className="text-center">
          <p className="text-secondary text-meta-md">Loading...</p>
        </div>
      </div>
    );
  }

  // For public routes, always render (don't wait for auth check)
  // This ensures login/signup pages load immediately

  return (
    <Routes>
      {/* Root redirect */}
      <Route path="/" element={<RootRedirect />} />

      {/* Public routes */}
      {publicRoutes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={
            // Wrap lazy-loaded components in Suspense
            // LoginPage is eagerly loaded, but SignupPage and UnauthorizedPage are lazy
            route.path === "/login" ? (
              <route.component />
            ) : (
              <LazyRouteWrapper>
                <route.component />
              </LazyRouteWrapper>
            )
          }
        />
      ))}

      {/* Protected routes */}
      <Route
        path="/*"
        element={
          isAuthenticated ? (
            <Routes>
              {/* Platform Admin routes with dedicated layout */}
              <Route
                path="platform-admin/*"
                element={
                  <LazyRouteWrapper>
                    <PlatformAdminLayout>
                      <Routes>
                        {platformAdminRoutes.map((route) => (
                          <Route
                            key={route.path}
                            path={route.path}
                            element={
                              <LazyRouteWrapper>
                                {route.requiredRole ? (
                                  <ProtectedRoute
                                    requiredRole={route.requiredRole}
                                  >
                                    <route.component />
                                  </ProtectedRoute>
                                ) : route.protected ? (
                                  <ProtectedRoute>
                                    <route.component />
                                  </ProtectedRoute>
                                ) : (
                                  <route.component />
                                )}
                              </LazyRouteWrapper>
                            }
                          />
                        ))}
                      </Routes>
                    </PlatformAdminLayout>
                  </LazyRouteWrapper>
                }
              />

              {/* Main dashboard routes */}
              <Route
                path="*"
                element={
                  <LazyRouteWrapper>
                    <DashboardLayout>
                      <Routes>
                        {dashboardRoutes.map((route) => (
                          <Route
                            key={route.path}
                            path={route.path}
                            element={
                              <LazyRouteWrapper>
                                {route.requiredRole ? (
                                  <ProtectedRoute
                                    requiredRole={route.requiredRole}
                                  >
                                    <route.component />
                                  </ProtectedRoute>
                                ) : route.protected ? (
                                  <ProtectedRoute>
                                    <route.component />
                                  </ProtectedRoute>
                                ) : (
                                  <route.component />
                                )}
                              </LazyRouteWrapper>
                            }
                          />
                        ))}

                        {/* Settings routes with nested layout */}
                        <Route
                          path="settings/*"
                          element={
                            <LazyRouteWrapper>
                              <SettingsLayout>
                                <Routes>
                                  {settingsRoutes.map((route) => (
                                    <Route
                                      key={route.path}
                                      path={route.path}
                                      element={
                                        <LazyRouteWrapper>
                                          {route.requiredRole ? (
                                            <ProtectedRoute
                                              requiredRole={route.requiredRole}
                                            >
                                              <route.component />
                                            </ProtectedRoute>
                                          ) : (
                                            <route.component />
                                          )}
                                        </LazyRouteWrapper>
                                      }
                                    />
                                  ))}
                                </Routes>
                              </SettingsLayout>
                            </LazyRouteWrapper>
                          }
                        />
                      </Routes>
                    </DashboardLayout>
                  </LazyRouteWrapper>
                }
              />
            </Routes>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
    </Routes>
  );
}

export default function App() {
  return (
    <ProviderComposition>
      <AppRoutes />
    </ProviderComposition>
  );
}
